import React, { useEffect, useRef, useState } from 'react';

import {
  Input,
  BaseButton,
  CloseButton,
  TopikList,
  TopikSpeakingList,
  useT,
} from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';

const getInputState = (focused, clicked) => {
  if (clicked) return 'active';
  if (focused) return 'focused';
  return 'default';
};

const ArrowToggleIcon = ({ open }) =>
  open ? (
    <ArrowTopIcon className='transition-transform duration-300 w-5 h-5' />
  ) : (
    <ArrowBottomIcon className='transition-transform duration-300 w-5 h-5' />
  );

const useDropdown = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return {
    open,
    ref,
    toggle: () => setOpen((o) => !o),
    close: () => setOpen(false),
  };
};

const TopikCertModal = ({ topikInfo, setTopikInfo, onClose }) => {
  const t = useT();
  const levelDd = useDropdown();
  const [dateFocused, setDateFocused] = useState(false);

  if (!topikInfo) return null;

  return (
    <>
      <div
        className='fixed inset-0 z-[999] bg-surface-black40'
        onClick={onClose}
      />

      <div className='fixed bottom-0 left-0 right-0 z-[1000] flex flex-col bg-grayscale-gray00 rounded-t-[30px] pt-4 px-6 pb-10 w-full'>
        <div className='pb-6'>
          <CloseButton onClick={onClose} size='w-8 h-8' type='light' />
        </div>

        <div className='flex flex-col gap-10 pb-[60px]'>
          <div className='flex flex-col items-start gap-2'>
            <div className='typo-title1 text-text-primary'>
              {t('Add certification', '자격증 추가')}
            </div>
            <div className='typo-b14 text-grayscale-gray04'>
              {t(
                'This information will be used in your resume.',
                '이 정보는 이력서에 사용됩니다.',
              )}
            </div>
          </div>

          <div className='flex flex-col gap-4 w-full'>
            {/* TOPIK Level */}
            <div className='relative w-full' ref={levelDd.ref}>
              <div className='flex flex-col gap-2'>
                <div className='typo-b12Strong text-text-placeholder'>
                  {topikInfo.type === 'topik'
                    ? t('TOPIK Level', 'TOPIK 급수')
                    : t('TOPIK speaking Level', 'TOPIK 말하기 급수')}
                </div>
                <Input
                  label=''
                  placeholder={t('Select level', '급수 선택')}
                  value={topikInfo.level}
                  readOnly
                  state={getInputState(false, levelDd.open)}
                  rightIcon={<ArrowToggleIcon open={levelDd.open} />}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={levelDd.toggle}
                  onRightIconClick={levelDd.toggle}
                />

                {levelDd.open && topikInfo.type === 'topik' && (
                  <div className='z-10 w-full'>
                    <TopikList
                      selectedOption={topikInfo.level}
                      onSelect={(opt) => {
                        setTopikInfo((prev) => ({ ...prev, level: opt }));
                        levelDd.close();
                      }}
                      isMyPage={true}
                    />
                  </div>
                )}

                {levelDd.open && topikInfo.type === 'topikSpeaking' && (
                  <div className='z-10 w-full'>
                    <TopikSpeakingList
                      selectedOption={topikInfo.level}
                      onSelect={(opt) => {
                        setTopikInfo((prev) => ({ ...prev, level: opt }));
                        levelDd.close();
                      }}
                      isMyPage={true}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Issue date */}
            <div className='flex flex-col gap-2 w-[180px]'>
              <div className='typo-b12Strong text-text-placeholder'>
                {t('Issue date', '발급일')}
              </div>
              <Input
                id='topik-date-input'
                label=''
                value={topikInfo.date}
                placeholder='YYYY / MM / DD'
                state={getInputState(false, dateFocused)}
                hideCount
                showStatusIcon={false}
                onChange={(e) =>
                  setTopikInfo((prev) => ({ ...prev, date: e.target.value }))
                }
                onFocus={() => setDateFocused(true)}
                onBlur={() => setDateFocused(false)}
              />
            </div>
          </div>
        </div>

        <BaseButton
          type='primary'
          size='l'
          hideIcon
          width='100%'
          onClick={onClose}
        >
          {t('Save', '저장')}
        </BaseButton>
      </div>
    </>
  );
};

export default TopikCertModal;
